import { useState } from "react";
import {
  ComposedModal,
  FileUploaderDropContainer,
  FileUploaderItem,
  FormGroup,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextArea,
} from "@carbon/react";
import i18next from "../../i18n";
import { useFileInputUploader } from "../../hooks";
import FileReader from "../FileReader/FileReader";

type Props = {
  open: boolean;
  comment: string;
  onCommentChange: (value: string) => void;
  /** S3 key of the optional supporting file (already uploaded), lifted to the parent so it can
   * ride the award payload alongside the reason. Empty string = no file. */
  fileUrl: string;
  onFileUrlChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** The award mutation is in flight — the dialog stays open with a loading, disabled primary
   * button so the buyer sees the award is being processed instead of the panel closing silently. */
  loading?: boolean;
};

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * "Accept offer" confirmation, split into two clearly separated sections: the award REASON
 * (a free-text note) and an optional SUPPORTING FILE (a document that backs the decision — a
 * quote comparison, an internal approval, etc.). The file is uploaded here via the shared
 * `useFileInputUploader` (BE-proxied `POST /utils/upload-s3`, returns an S3 key); only the key
 * is lifted to the parent and rides the existing JSON award payload — the award endpoint stays
 * unchanged. Confirming calls `onConfirm`, which the parent wires to the award mutation.
 */
const AwardAcceptDialog = ({
  open,
  comment,
  onCommentChange,
  fileUrl,
  onFileUrlChange,
  onConfirm,
  onCancel,
  loading = false,
}: Props) => {
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");
  const { mutate: uploadFile, isPending: isUploading } = useFileInputUploader();

  const clearFile = () => {
    setPendingFileName(null);
    setFileError("");
    onFileUrlChange("");
  };

  return (
    <ComposedModal
      open={open}
      aria-label={i18next.t("tenders.data.accept")}
      onClose={onCancel}
      preventCloseOnClickOutside={loading}
    >
      <ModalHeader title={i18next.t("tenders.data.accept")} />
      <ModalBody>
        <FormGroup
          legendText={i18next.t("award.reasonSectionTitle")}
          style={{ marginBottom: "1.5rem" }}
        >
          <p
            style={{
              fontSize: "0.75rem",
              color: "var(--cds-text-secondary, #525252)",
              marginBottom: "0.75rem",
            }}
          >
            {i18next.t("tenders.data.acceptQuestion")}
          </p>
          <TextArea
            id="offerSelectionComment"
            labelText={i18next.t("tenders.form.offerSelectionComment")}
            value={comment}
            maxLength={999}
            rows={4}
            placeholder={i18next.t("award.reasonPlaceholder")}
            onChange={(e) => {
              if (e.target.value.length >= 999) return;
              onCommentChange(e.target.value);
            }}
          />
        </FormGroup>

        <FormGroup legendText={i18next.t("award.fileSectionTitle")}>
          <p
            style={{
              fontSize: "0.75rem",
              color: "var(--cds-text-secondary, #525252)",
              marginBottom: "0.75rem",
            }}
          >
            {i18next.t("award.fileHelper")}
          </p>
          <FileUploaderDropContainer
            labelText={
              isUploading
                ? i18next.t("cta.uploading")
                : i18next.t("award.fileUploadLabel")
            }
            onAddFiles={(_event, { addedFiles }) => {
              const file = addedFiles?.[0];
              if (!file) return;
              if (file.size > MAX_FILE_SIZE_BYTES) {
                setFileError(i18next.t("award.fileTooLarge"));
                return;
              }
              setFileError("");
              setPendingFileName(file.name);
              uploadFile(file, {
                onSuccess: (key: string) => onFileUrlChange(key),
                onError: () => {
                  setFileError(i18next.t("award.fileUploadFailed"));
                  setPendingFileName(null);
                },
              });
            }}
          />
          {pendingFileName && (
            <FileUploaderItem
              name={pendingFileName}
              status={isUploading ? "uploading" : "complete"}
              onDelete={clearFile}
            />
          )}
          {fileUrl && !pendingFileName && (
            <div style={{ marginTop: "0.5rem" }}>
              <FileReader fileKey={fileUrl} text={i18next.t("cta.download")} />
            </div>
          )}
          {fileError && (
            <p
              style={{
                fontSize: "0.75rem",
                color: "var(--cds-text-error, #da1e28)",
                marginTop: "0.5rem",
              }}
            >
              {fileError}
            </p>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter
        secondaryButtonText={i18next.t("cta.cancel")}
        primaryButtonText={
          loading ? i18next.t("cta.loading") : i18next.t("tenders.data.accept")
        }
        primaryButtonDisabled={loading || isUploading}
        onRequestSubmit={onConfirm}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default AwardAcceptDialog;
